"use client"

import { useEffect, useState } from "react"
import {
  Plus, Database, Edit, Trash2, TestTube,
} from "lucide-react"

import { Button } from "../ui/button"
import EmptyState from "../ui/EmptyState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog"

import { useToast } from "../../hooks/use-toast"
import { useOrganization } from "../../contexts/OrganizationContext"
import { DatabaseConnectionDialog } from "../database-connection-dialog/database-connection-dialog"
import credentialService from "../../services/credential.service"
import { PRIMARY, BUTTON_SIZE } from "../../lib/buttonStyles"

const ENGINES = ["postgres", "mysql", "sqlserver", "mongodb", "redis"]

export function DatabaseConnections() {
  const { toast } = useToast()
  const { currentAccount } = useOrganization()
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  // Guardar currentAccountId en localStorage para que credential service pueda usarlo
  useEffect(() => {
    if (currentAccount?.id) {
      localStorage.setItem('currentAccountId', currentAccount.id)
    }
  }, [currentAccount])

  useEffect(() => {
    if (currentAccount?.id) {
      reload(true)
    }
  }, [currentAccount])

  const reload = async (initial = false) => {
    initial ? setLoading(true) : setRefreshing(true)
    try {
      // Cargar credenciales de bases de datos relacionales y URI
      const [relationalResult, uriResult] = await Promise.all([
        credentialService.listRelationalDatabases(),
        credentialService.listUriDatabases(),
      ])
      
      const relationalList = relationalResult.success ? relationalResult.data : []
      const uriList = uriResult.success ? uriResult.data : []
      
      // El endpoint de listar solo devuelve datos básicos, 
      // necesitamos obtener el detalle de cada credencial
      const relationalDetails = await Promise.all(
        relationalList.map(async (cred) => {
          try {
            const detail = await credentialService.getCredential('relational_database', cred.id)
            return detail.success ? detail.data : cred
          } catch {
            return cred
          }
        })
      )
      
      const uriDetails = await Promise.all(
        uriList.map(async (cred) => {
          try {
            const detail = await credentialService.getCredential('uri_database', cred.id)
            return detail.success ? detail.data : cred
          } catch {
            return cred
          }
        })
      )
      
      // Combinar y eliminar duplicados por ID
      const allCredentials = [...relationalDetails, ...uriDetails]
      const uniq = Object.values(
        allCredentials.reduce((acc, cred) => {
          acc[cred.id] = {
            id: cred.id,
            name: cred.name,
            description: cred.description,
            credential_type: cred.credential_type,
            engine: cred.engine || (cred.credential_type === 'uri_database' ? 'uri' : 'unknown'),
            host: cred.host,
            port: cred.port,
            database: cred.database_name || cred.database,
            connection_uri: cred.connection_uri,
            lastTested: cred.last_tested || cred.lastTested,
            created_at: cred.created_at,
          }
          return acc
        }, {})
      )
      setConnections(uniq)
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }

  const handleSave = async (data) => {
    setSaving(true)
    try {
      // Si estamos editando, primero eliminar la credencial existente
      if (editing) {
        const credType = editing.credential_type || (editing.connection_uri ? 'uri_database' : 'relational_database')
        await credentialService.deleteCredential(credType, editing.id)
      }
      
      // Crear la nueva credencial
      const isUri = data.type === 'mongodb' || data.type === 'redis' || data.connection_uri
      const credentialData = isUri
        ? {
            name: data.name,
            description: data.description || `Database connection for ${data.type}`,
            connection_uri: data.connection_uri,
            engine: data.type, // Engine es requerido: mongodb, redis
          }
        : {
            name: data.name,
            description: data.description || `Database connection for ${data.type}`,
            host: data.host,
            port: data.port ? Number(data.port) : undefined,
            database_name: data.database,
            username: data.user,
            password: data.password,
          }
      
      const result = isUri
        ? await credentialService.createUriDatabase(credentialData)
        : await credentialService.createRelationalDatabase(credentialData)
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save connection')
      }
      
      toast({ 
        title: editing ? "Connection updated successfully" : "Connection added successfully",
        description: "Your database connection has been saved."
      })
      await reload()
      
      // Solo cerrar si todo fue exitoso
      setDialogOpen(false)
      setEditing(null)
    } catch (e) {
      toast({ 
        title: "Error al guardar", 
        description: e.message, 
        variant: "destructive" 
      })
      // No cerrar el diálogo para que el usuario pueda corregir
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      setBusyId(deleteId)
      // Encontrar la credencial para obtener su tipo
      const credential = connections.find(c => c.id === deleteId)
      const credType = credential?.credential_type || (credential?.connection_uri ? 'uri_database' : 'relational_database')
      
      const result = await credentialService.deleteCredential(credType, deleteId)
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete connection')
      }
      
      toast({ title: "Connection deleted" })
      await reload()
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" })
    } finally {
      setBusyId(null); setDeleteId(null)
    }
  }

  const hostDisplay = (c) =>
    c.connection_uri
      ? c.connection_uri
      : c.host
        ? `${c.host}:${c.port ?? ""}`
        : "-"

  const dbDisplay = (c) =>
    c.database || (c.connection_uri ? "(URI)" : "-")
  
  const hostPreview = (str, len = 36) => {
  if (!str) return "-"
  return str.length > len ? str.slice(0, len - 3) + "..." : str
}


  return (
    <div className="space-y-6 p-4 min-h-screen sm:space-y-8 sm:p-8">

      {refreshing && (
        <div className="fixed top-4 right-4 z-50">
          <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full" />
        </div>
      )}

      {/* La pestaña hermana de API Keys, con la misma forma: encabezado de
          subsección y su acción en una fila, el conteo de insignia, y la
          tabla solo cuando hay algo que poner en ella. Tenía el mismo título
          de 24px repitiendo la pestaña y la misma tarjeta de media pantalla
          para un número. */}
      <div className="space-y-6 w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">Database Connections</h2>
              {connections.length > 0 && (
                <span className="rounded-full border border-white/10 px-2 py-0.5 text-xs text-gray-400">
                  {connections.length}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Databases this account can read from and write to
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true) }}
            className={`flex w-full items-center justify-center sm:w-auto ${PRIMARY}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Connection
          </Button>
        </div>

{!loading && connections.length === 0 ? (
  <EmptyState
    icon={<Database size={28} aria-hidden="true" />}
    title="No database connections yet"
    description="Connect a database so your agents and workflows can read from it and write to it."
    action={{
      label: "Add connection",
      onClick: () => { setEditing(null); setDialogOpen(true) },
    }}
  />
) : (
<div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0F1117]">
  <Table className="min-w-full divide-y divide-white/10">
    <TableHeader>
      <TableRow>
        <TableHead className="text-slate-300 font-semibold py-4">Name</TableHead>
        <TableHead className="text-slate-300 font-semibold">Database</TableHead>
        <TableHead className="text-slate-300 font-semibold">Last Tested</TableHead>
        <TableHead className="text-slate-300 font-semibold text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {loading ? (
        <TableRow>
          <TableCell colSpan={4} className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-purple-500 mx-auto" />
          </TableCell>
        </TableRow>
      ) : (
        connections.map((c) => (
          <TableRow key={c.id} className="hover:bg-white/5 transition">
            {/* Name + Icon */}
            <TableCell className="font-medium text-white py-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#7c3aed]/20">
                  <Database className="w-5 h-5 text-[#7c3aed]" />
                </span>
                <span className="font-bold">{c.name}</span>
              </div>
            </TableCell>
            {/* Database */}
            <TableCell className="font-mono text-sm text-slate-300">{dbDisplay(c)}</TableCell>
            {/* Last tested */}
            <TableCell className="text-slate-400">{c.lastTested ?? "Never"}</TableCell>
            {/* Actions */}
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-gray-800/80 rounded-full">
                    <span className="flex items-center gap-1">
                      <span className="w-1 h-1 bg-current rounded-full" />
                      <span className="w-1 h-1 bg-current rounded-full" />
                      <span className="w-1 h-1 bg-current rounded-full" />
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl">
                  <DropdownMenuItem
                    className="hover:bg-gray-800 text-white"
                    onClick={() => { setEditing(c); setDialogOpen(true) }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem className="hover:bg-gray-800 text-white">
                    <TestTube className="w-4 h-4 mr-2" />
                    Test Connection
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={busyId === c.id}
                    className="text-red-400 hover:bg-red-900/60 hover:text-red-300 rounded-lg"
                    onSelect={(e) => e.preventDefault()}
                    onClick={() => setDeleteId(c.id)}
                  >
                    {busyId === c.id
                      ? <span className="animate-spin h-4 w-4 mr-2 rounded-full border-2 border-current border-t-transparent" />
                      : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
</div>
)}

        {/* modal alta / edición */}
        <DatabaseConnectionDialog
          open={dialogOpen}
          onOpenChange={(o) => { if (!o) setEditing(null); setDialogOpen(o) }}
          connection={editing}
          onSave={handleSave}
          saving={saving}
        />

        {/* AlertDialog Confirm Delete */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-black/80 backdrop-blur-2xl border border-purple-500/20 rounded-3xl max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-purple-300">Delete Connection</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This action is <span className="text-red-400 font-semibold">irreversible</span>. The application will no longer be able to use this connection. Are you sure?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6 flex justify-end space-x-4">
              <AlertDialogCancel asChild>
                <Button variant="secondary">Cancel</Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button className={`${BUTTON_SIZE} bg-red-600 text-white hover:bg-red-700`} onClick={confirmDelete}>
                  Delete
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
